// src/context/AuthContext.tsx
"use client";

import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useCallback,
  PropsWithChildren,
} from "react";
import { configureAmplify } from "@/lib/amplify-config";
import {
  login as authLogin,
  logout as authLogout,
  checkAuthStatus,
  subscribeToAuthEvents,
  clearLegacyTokens,
  getAuthErrorMessage,
  isUserNotConfirmedError,
  AuthUserProfile,
} from "@/lib/auth-service";
import * as Sentry from "@sentry/react";
import { identifyUser, resetUser } from "@/lib/analytics";
import { AuthContextType, UserProfile } from "@/types/auth";

// Inicializar Amplify una vez al cargar el módulo
if (typeof window !== "undefined") {
  configureAmplify();
}

// Crear el contexto con valores predeterminados
const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  error: null,
  isAuthenticated: () => false,
  login: async () => {},
  logout: () => {},
  clearError: () => {},
  updateUser: () => {},
});

export function AuthProvider({
  children,
}: PropsWithChildren): React.ReactElement {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Verificar estado de autenticación al montar
  useEffect(() => {
    const initAuth = async (): Promise<void> => {
      // Limpiar tokens legacy de cualquier sistema anterior
      clearLegacyTokens();

      try {
        const authUser = await checkAuthStatus();
        if (authUser) {
          const profile = authUserToProfile(authUser);
          setUser(profile);
          Sentry.setUser({ id: profile.id, email: profile.email });
          identifyUser(profile.id, { email: profile.email, name: profile.name });
        }
      } catch {
        // Usuario no autenticado, estado normal
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  // Suscribirse a eventos de Amplify Hub para sincronización entre pestañas
  useEffect(() => {
    const unsubscribe = subscribeToAuthEvents((event) => {
      switch (event) {
        case "signedIn":
          // Re-verificar estado de autenticación
          checkAuthStatus().then((authUser) => {
            if (authUser) {
              const profile = authUserToProfile(authUser);
              setUser(profile);
              Sentry.setUser({ id: profile.id, email: profile.email });
              identifyUser(profile.id, { email: profile.email, name: profile.name });
            }
          });
          break;
        case "signedOut":
          setUser(null);
          Sentry.setUser(null);
          resetUser();
          break;
        case "tokenRefreshFailure":
          // Token expirado y no se pudo refrescar
          setUser(null);
          Sentry.setUser(null);
          resetUser();
          break;
      }
    });

    return () => unsubscribe();
  }, []);

  // Convertir AuthUserProfile a UserProfile
  const authUserToProfile = (authUser: AuthUserProfile): UserProfile => ({
    id: authUser.id,
    email: authUser.email,
    name: authUser.name,
  });

  // Función de login
  const login = useCallback(
    async (email: string, password: string): Promise<void> => {
      setError(null);
      setLoading(true);

      try {
        const authUser = await authLogin(email, password);
        const profile = authUserToProfile(authUser);
        setUser(profile);
        Sentry.setUser({ id: profile.id, email: profile.email });
        identifyUser(profile.id, { email: profile.email, name: profile.name });
      } catch (err) {
        // No fijar error para UserNotConfirmedException:
        // el formulario de login muestra el banner de verificación.
        if (!isUserNotConfirmedError(err)) {
          const errorMessage = getAuthErrorMessage(err, "es");
          setError(errorMessage);
        }
        throw err; // Re-lanzar para que el formulario pueda manejarlo
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Función de logout
  const logout = useCallback((): void => {
    setLoading(true);
    // Limpiar cache de consent
    sessionStorage.removeItem("consent_checked");
    authLogout()
      .then(() => {
        setUser(null);
        Sentry.setUser(null);
        resetUser();
        // Redirigir a login (navegación dura, confiable en export estático)
        window.location.href = "/es/login";
      })
      .catch(() => {
        // Aún así limpiar el estado local
        setUser(null);
        window.location.href = "/es/login";
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  // Limpiar error
  const clearError = useCallback((): void => {
    setError(null);
  }, []);

  // Verificar si el usuario está autenticado
  const isAuthenticated = useCallback((): boolean => !!user, [user]);

  // Actualizar usuario localmente (sin re-fetch de Cognito)
  const updateUser = useCallback((updates: Partial<UserProfile>): void => {
    setUser((current) => (current ? { ...current, ...updates } : null));
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        error,
        isAuthenticated,
        login,
        logout,
        clearError,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// Hook personalizado para usar el contexto
export function useAuth(): AuthContextType {
  return useContext(AuthContext);
}

// Re-exportar tipos para compatibilidad
export type { UserProfile } from "@/types/auth";
